import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  UploadFile,
  AutoAwesome,
  SearchRounded,
  TravelExploreRounded,
  PictureAsPdfRounded,
  ZoomInRounded,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { documentApi, vehicleApi, vehicleMaintenanceApi } from '../../services/api';
import { Vehicle } from '../../types/vehicle';

// The type of maintenance record a document actually is. Lives per-item, since a mixed batch
// can contain a different type per file (either picked upfront, or guessed by AI per document).
type BatchRecordType = 'insurance' | 'inspection' | 'tax' | '';

// The mode the whole batch runs in: either one fixed type for every file (existing behavior),
// or 'auto' — let AI classify each document individually.
type BatchImportMode = BatchRecordType | 'auto';

type BatchItemStatus = 'queued' | 'processing' | 'ready' | 'failed' | 'created';

interface BatchItem {
  file: File;
  previewUrl: string;
  status: BatchItemStatus;
  error?: string;
  values: Record<string, string>;
  matchedVehicleId: number | null;
  recordType: BatchRecordType;
}

interface BatchMaintenanceImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const RECORD_TYPE_TO_DOCUMENT_TYPE: Record<Exclude<BatchRecordType, ''>, string> = {
  insurance: 'VEHICLE_INSURANCE',
  inspection: 'VEHICLE_INSPECTION',
  tax: 'VEHICLE_TAX',
};

// Reverse lookup for the auto-detect schema's own documentType classification (INSURANCE/INSPECTION/TAX).
const DOCUMENT_TYPE_TO_RECORD_TYPE: Record<string, Exclude<BatchRecordType, ''>> = {
  INSURANCE: 'insurance',
  INSPECTION: 'inspection',
  TAX: 'tax',
};

const AUTO_DETECT_DOCUMENT_TYPE = 'VEHICLE_MAINTENANCE_AUTO';

const REQUIRED_FIELDS_BY_TYPE: Record<Exclude<BatchRecordType, ''>, string[]> = {
  insurance: ['policyNumber', 'providerCompany', 'startDate', 'expiryDate'],
  inspection: ['inspectionDate', 'expiryDate', 'inspectionCenter'],
  tax: ['taxType', 'paymentDate', 'expiryDate'],
};

const normalizePlate = (value?: string | null) => (value || '').replace(/[\s-]/g, '').toUpperCase();
const normalizeVin = (value?: string | null) => (value || '').replace(/\s/g, '').toUpperCase();

const CONCURRENCY = 2;

export const BatchMaintenanceImportDialog: React.FC<BatchMaintenanceImportDialogProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [importMode, setImportMode] = useState<BatchImportMode>('');
  const [items, setItems] = useState<BatchItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [creatingAll, setCreatingAll] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const { data: vehicles = [] } = useQuery('vehicles', vehicleApi.getAll, { enabled: open });

  // Object URLs are created per file so the actual document can be viewed while reviewing (not
  // just the filename). Keep a ref to the latest items so we can revoke them all on unmount,
  // even if the dialog is torn down without going through handleClose/reset.
  const itemsRef = useRef<BatchItem[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  useEffect(() => {
    return () => {
      itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, []);

  const vehicleOptionLabel = (vehicle: Vehicle) =>
    vehicle.licensePlate ? `${vehicle.licensePlate} - ${vehicle.make} ${vehicle.model}` : `${vehicle.make} ${vehicle.model}`;

  const matchVehicle = (plate?: string, vin?: string): Vehicle | null => {
    const normalizedPlate = normalizePlate(plate);
    const normalizedVin = normalizeVin(vin);
    if (normalizedPlate) {
      const byPlate = vehicles.find((vehicle) => normalizePlate(vehicle.licensePlate) === normalizedPlate);
      if (byPlate) return byPlate;
    }
    if (normalizedVin) {
      const byVin = vehicles.find((vehicle) => normalizeVin(vehicle.vin) === normalizedVin);
      if (byVin) return byVin;
    }
    return null;
  };

  const reset = () => {
    items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setImportMode('');
    setItems([]);
    setProcessing(false);
    setCreatingAll(false);
    setPreviewIndex(null);
  };

  const handleClose = () => {
    if (processing || creatingAll) return;
    reset();
    onClose();
  };

  const handleFilesSelected = (files: File[]) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${t('maintenance.extract.fileTooLarge', 'Document size must be less than 10MB')}: ${file.name}`);
        continue;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${t('maintenance.extract.invalidFileType', 'Upload a PDF, JPEG, PNG, or WEBP file')}: ${file.name}`);
        continue;
      }
      validFiles.push(file);
    }
    setItems((current) => [
      ...current,
      ...validFiles.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'queued' as BatchItemStatus,
        values: {},
        matchedVehicleId: null,
        recordType: '' as BatchRecordType,
      })),
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((current) => {
      URL.revokeObjectURL(current[index].previewUrl);
      return current.filter((_, i) => i !== index);
    });
  };

  // Runs extraction for one item against the given document type and updates its state with the
  // result. Used both for the initial batch pass and for re-running a single item after the user
  // corrects its detected record type.
  const runExtraction = async (
    index: number,
    documentType: string,
    file: File,
    presetRecordType?: BatchRecordType
  ) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, status: 'processing', recordType: presetRecordType ?? item.recordType } : item
      )
    );
    try {
      const extraction = await documentApi.uploadAndExtract({ documentType, frontFile: file });
      const valuesByField: Record<string, string> = {};
      for (const field of extraction.extraction?.fields || []) {
        valuesByField[field.fieldName] = field.normalizedValue || field.fieldValue || '';
      }
      const matched = matchVehicle(valuesByField.licensePlate, valuesByField.vin);
      const classifiedType = DOCUMENT_TYPE_TO_RECORD_TYPE[valuesByField.documentType];
      setItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                status: 'ready',
                values: valuesByField,
                matchedVehicleId: matched?.id ?? null,
                recordType: classifiedType || item.recordType,
              }
            : item
        )
      );
    } catch (error: any) {
      setItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, status: 'failed', error: error.response?.data?.error || error.message || t('common.failed', 'Failed') }
            : item
        )
      );
    }
  };

  const handleProcess = async () => {
    if (!importMode || items.length === 0 || processing) return;
    const documentType = importMode === 'auto' ? AUTO_DETECT_DOCUMENT_TYPE : RECORD_TYPE_TO_DOCUMENT_TYPE[importMode];
    const presetRecordType = importMode === 'auto' ? undefined : importMode;
    setProcessing(true);

    const files = items.map((item) => item.file);
    let nextIndex = 0;

    const runWorker = async () => {
      while (nextIndex < files.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        await runExtraction(currentIndex, documentType, files[currentIndex], presetRecordType);
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, runWorker));
    setProcessing(false);
  };

  // Re-runs extraction for a single item using its (possibly user-corrected) record type,
  // against the specific type's schema rather than the generic auto-detect one.
  const handleReprocessItem = (index: number) => {
    const item = items[index];
    if (!item.recordType || item.status === 'processing') return;
    runExtraction(index, RECORD_TYPE_TO_DOCUMENT_TYPE[item.recordType], item.file, item.recordType);
  };

  const isItemValid = (item: BatchItem) => {
    if (!item.recordType || item.matchedVehicleId === null) return false;
    return REQUIRED_FIELDS_BY_TYPE[item.recordType].every((field) => (item.values[field] || '').trim().length > 0);
  };

  const createRecordForItem = async (index: number) => {
    const item = items[index];
    if (!item.recordType || item.matchedVehicleId === null || !isItemValid(item)) return;
    const vehicleId = String(item.matchedVehicleId);
    const values = item.values;
    try {
      if (item.recordType === 'insurance') {
        await vehicleMaintenanceApi.createInsuranceRecord(vehicleId, {
          policyNumber: values.policyNumber,
          providerCompany: values.providerCompany,
          startDate: values.startDate,
          expiryDate: values.expiryDate,
          cost: values.cost ? Number(values.cost) : null,
        });
      } else if (item.recordType === 'inspection') {
        await vehicleMaintenanceApi.createInspectionRecord(vehicleId, {
          inspectionDate: values.inspectionDate,
          expiryDate: values.expiryDate,
          inspectionCenter: values.inspectionCenter,
          cost: values.cost ? Number(values.cost) : null,
        });
      } else if (item.recordType === 'tax') {
        await vehicleMaintenanceApi.createTaxRecord(vehicleId, {
          taxType: values.taxType,
          paymentDate: values.paymentDate,
          expiryDate: values.expiryDate,
          amount: values.amount ? Number(values.amount) : null,
        });
      }
      setItems((prev) => prev.map((current, i) => (i === index ? { ...current, status: 'created' } : current)));
      queryClient.invalidateQueries('vehicleMaintenanceDashboard');
      queryClient.invalidateQueries(['vehicleMaintenanceOverview', vehicleId]);
      onImported();
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || t('maintenance.batchImport.createFailed', 'Failed to create record'));
    }
  };

  const handleCreateAll = async () => {
    setCreatingAll(true);
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].status === 'ready' && isItemValid(items[i])) {
        // eslint-disable-next-line no-await-in-loop
        await createRecordForItem(i);
      }
    }
    setCreatingAll(false);
    toast.success(t('maintenance.batchImport.batchComplete', 'Import finished. Please review any remaining items.'));
  };

  const updateItemValue = (index: number, field: string, value: string) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, values: { ...item.values, [field]: value } } : item)));
  };

  const updateItemVehicle = (index: number, vehicleId: number | null) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, matchedVehicleId: vehicleId } : item)));
  };

  // Images open in the in-dialog lightbox below; PDFs open in a new tab and use the browser's
  // own viewer, which is a better reading experience than trying to embed one.
  const openPreview = (index: number) => {
    const item = items[index];
    if (item.file.type === 'application/pdf') {
      window.open(item.previewUrl, '_blank', 'noopener,noreferrer');
    } else {
      setPreviewIndex(index);
    }
  };

  const renderThumbnail = (item: BatchItem, index: number) => (
    <Tooltip title={t('maintenance.batchImport.viewDocument', 'View document')}>
      <Box
        onClick={() => openPreview(index)}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          '&:hover .zoomOverlay': { opacity: 1 },
        }}
      >
        {item.file.type === 'application/pdf' ? (
          <PictureAsPdfRounded fontSize="small" color="error" />
        ) : (
          <Box component="img" src={item.previewUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        <Box
          className="zoomOverlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.45)',
            opacity: 0,
            transition: 'opacity 0.15s',
          }}
        >
          <ZoomInRounded sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
      </Box>
    </Tooltip>
  );

  const updateItemRecordType = (index: number, value: BatchRecordType) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, recordType: value } : item)));
  };

  const handleRematch = (index: number) => {
    const item = items[index];
    const matched = matchVehicle(item.values.licensePlate, item.values.vin);
    updateItemVehicle(index, matched?.id ?? null);
    if (!matched) {
      toast.error(t('maintenance.batchImport.stillNoMatch', 'Still no vehicle matches that plate/VIN'));
    }
  };

  const readyCount = useMemo(() => items.filter((item) => item.status === 'ready').length, [items]);
  const hasProcessed = items.some((item) => item.status === 'ready' || item.status === 'failed' || item.status === 'created');

  const renderFieldsForItem = (item: BatchItem, index: number) => {
    const disabled = item.status === 'created';
    if (item.recordType === 'insurance') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('maintenance.fields.policyNumber', 'Policy number')}
              value={item.values.policyNumber || ''}
              onChange={(e) => updateItemValue(index, 'policyNumber', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('maintenance.fields.providerCompany', 'Provider / company')}
              value={item.values.providerCompany || ''}
              onChange={(e) => updateItemValue(index, 'providerCompany', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t('maintenance.fields.cost', 'Cost')}
              type="number"
              value={item.values.cost || ''}
              onChange={(e) => updateItemValue(index, 'cost', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.startDate', 'Start date')}
              type="date"
              value={item.values.startDate || ''}
              onChange={(e) => updateItemValue(index, 'startDate', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.expiryDate', 'Expiry date')}
              type="date"
              value={item.values.expiryDate || ''}
              onChange={(e) => updateItemValue(index, 'expiryDate', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      );
    }
    if (item.recordType === 'inspection') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.inspectionCenter', 'Inspection center')}
              value={item.values.inspectionCenter || ''}
              onChange={(e) => updateItemValue(index, 'inspectionCenter', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.cost', 'Cost')}
              type="number"
              value={item.values.cost || ''}
              onChange={(e) => updateItemValue(index, 'cost', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.inspectionDate', 'Inspection date')}
              type="date"
              value={item.values.inspectionDate || ''}
              onChange={(e) => updateItemValue(index, 'inspectionDate', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.expiryDate', 'Expiry date')}
              type="date"
              value={item.values.expiryDate || ''}
              onChange={(e) => updateItemValue(index, 'expiryDate', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      );
    }
    if (item.recordType === 'tax') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.taxType', 'Tax type')}
              value={item.values.taxType || ''}
              onChange={(e) => updateItemValue(index, 'taxType', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.amount', 'Amount')}
              type="number"
              value={item.values.amount || ''}
              onChange={(e) => updateItemValue(index, 'amount', e.target.value)}
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.paymentDate', 'Payment date')}
              type="date"
              value={item.values.paymentDate || ''}
              onChange={(e) => updateItemValue(index, 'paymentDate', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('maintenance.fields.expiryDate', 'Expiry date')}
              type="date"
              value={item.values.expiryDate || ''}
              onChange={(e) => updateItemValue(index, 'expiryDate', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('maintenance.batchImport.title', 'Batch Import Maintenance Documents')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'maintenance.batchImport.hint',
            'Upload multiple documents, even for different vehicles and different document types. Each one is processed with AI and matched to a vehicle by license plate or VIN for you to review before creating.'
          )}
        </Typography>

        <TextField
          select
          label={t('maintenance.batchImport.recordType', 'Record type')}
          value={importMode}
          onChange={(e) => {
            setImportMode(e.target.value as BatchImportMode);
            setItems([]);
          }}
          size="small"
          sx={{ width: 280, mb: 2 }}
          disabled={hasProcessed}
        >
          <MenuItem value="auto">{t('maintenance.batchImport.autoDetect', 'Auto-detect (mixed batch)')}</MenuItem>
          <MenuItem value="insurance">{t('maintenance.attachmentOwner.insurance', 'Insurance')}</MenuItem>
          <MenuItem value="inspection">{t('maintenance.attachmentOwner.inspection', 'Inspection / MOT')}</MenuItem>
          <MenuItem value="tax">{t('maintenance.attachmentOwner.tax', 'Tax')}</MenuItem>
        </TextField>

        {importMode && !hasProcessed && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {t('maintenance.batchImport.chooseFiles', 'Choose Documents')}
              <input
                type="file"
                hidden
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = '';
                  if (files.length > 0) handleFilesSelected(files);
                }}
              />
            </Button>
          </Box>
        )}

        {items.length > 0 && !hasProcessed && (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            {items.map((item, index) => (
              <Stack key={`${item.file.name}-${index}`} direction="row" alignItems="center" spacing={1}>
                {renderThumbnail(item, index)}
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {item.file.name}
                </Typography>
                <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}

        {items.length > 0 && !hasProcessed && (
          <Button
            variant="contained"
            size="small"
            startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
            onClick={handleProcess}
            disabled={processing}
            sx={{ textTransform: 'none' }}
          >
            {processing
              ? t('maintenance.batchImport.processing', 'Processing...')
              : t('maintenance.batchImport.startImport', 'Start Import')}
          </Button>
        )}

        {hasProcessed && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('maintenance.batchImport.reviewTitle', 'Review before creating')}
            </Typography>
            <Stack spacing={2}>
              {items.map((item, index) => (
                <Paper key={`${item.file.name}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    {renderThumbnail(item, index)}
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {item.file.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={t(`maintenance.batchImport.status.${item.status}`, item.status)}
                      color={
                        item.status === 'created'
                          ? 'success'
                          : item.status === 'failed'
                          ? 'error'
                          : item.matchedVehicleId
                          ? 'info'
                          : 'warning'
                      }
                    />
                  </Stack>
                  {item.status === 'failed' ? (
                    <Alert severity="error">{item.error}</Alert>
                  ) : (
                    <>
                      {importMode === 'auto' && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          sx={{ mb: 1.5 }}
                        >
                          <TextField
                            select
                            size="small"
                            label={t('maintenance.batchImport.detectedType', 'Detected as')}
                            value={item.recordType}
                            onChange={(e) => updateItemRecordType(index, e.target.value as BatchRecordType)}
                            disabled={item.status === 'created' || item.status === 'processing'}
                            sx={{ minWidth: 200 }}
                          >
                            <MenuItem value="insurance">{t('maintenance.attachmentOwner.insurance', 'Insurance')}</MenuItem>
                            <MenuItem value="inspection">{t('maintenance.attachmentOwner.inspection', 'Inspection / MOT')}</MenuItem>
                            <MenuItem value="tax">{t('maintenance.attachmentOwner.tax', 'Tax')}</MenuItem>
                          </TextField>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              item.status === 'processing' ? <CircularProgress size={14} /> : <AutoAwesome fontSize="small" />
                            }
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            disabled={!item.recordType || item.status === 'created' || item.status === 'processing'}
                            onClick={() => handleReprocessItem(index)}
                          >
                            {t('maintenance.batchImport.reprocess', 'Reprocess as this type')}
                          </Button>
                        </Stack>
                      )}
                      {item.matchedVehicleId === null && (
                        <Box
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                            <TravelExploreRounded sx={{ fontSize: 18, color: 'warning.dark' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                              {t('maintenance.batchImport.noMatchTitle', "Couldn't auto-match this document")}
                            </Typography>
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                              label={t('maintenance.batchImport.extractedLicensePlate', 'Extracted plate')}
                              value={item.values.licensePlate || ''}
                              onChange={(e) => updateItemValue(index, 'licensePlate', e.target.value)}
                              size="small"
                              fullWidth
                              disabled={item.status === 'created'}
                              sx={{ bgcolor: 'background.paper' }}
                            />
                            <TextField
                              label={t('maintenance.batchImport.extractedVin', 'Extracted VIN')}
                              value={item.values.vin || ''}
                              onChange={(e) => updateItemValue(index, 'vin', e.target.value)}
                              size="small"
                              fullWidth
                              disabled={item.status === 'created'}
                              sx={{ bgcolor: 'background.paper' }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              startIcon={<SearchRounded />}
                              sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 2, flexShrink: 0 }}
                              disabled={item.status === 'created' || (!item.values.licensePlate && !item.values.vin)}
                              onClick={() => handleRematch(index)}
                            >
                              {t('maintenance.batchImport.findVehicle', 'Find vehicle')}
                            </Button>
                          </Stack>
                        </Box>
                      )}
                      <Autocomplete
                        options={vehicles}
                        getOptionLabel={vehicleOptionLabel}
                        value={vehicles.find((vehicle) => vehicle.id === item.matchedVehicleId) || null}
                        onChange={(_, value) => updateItemVehicle(index, value ? value.id : null)}
                        disabled={item.status === 'created'}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={t('maintenance.batchImport.vehicle', 'Vehicle')}
                            sx={{ mb: 1 }}
                            helperText={
                              item.matchedVehicleId === null
                                ? t('maintenance.batchImport.noMatch', 'Or pick the vehicle manually below')
                                : undefined
                            }
                          />
                        )}
                      />
                      {renderFieldsForItem(item, index)}
                      {item.status !== 'created' && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1, textTransform: 'none' }}
                          disabled={!isItemValid(item) || creatingAll}
                          onClick={() => createRecordForItem(index)}
                        >
                          {t('common.create', 'Create')}
                        </Button>
                      )}
                    </>
                  )}
                </Paper>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={processing || creatingAll}>
          {t('common.close', 'Close')}
        </Button>
        {hasProcessed && readyCount > 0 && (
          <Button variant="contained" onClick={handleCreateAll} disabled={creatingAll || processing}>
            {creatingAll
              ? t('maintenance.batchImport.processing', 'Processing...')
              : t('maintenance.batchImport.createAll', 'Create All')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    <Dialog open={previewIndex !== null} onClose={() => setPreviewIndex(null)} maxWidth="lg">
      <DialogContent sx={{ p: 0, lineHeight: 0, bgcolor: 'common.black' }}>
        {previewIndex !== null && (
          <Box
            component="img"
            src={items[previewIndex]?.previewUrl}
            alt={items[previewIndex]?.file.name}
            sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh', mx: 'auto' }}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};
